import React from "react";
import { Text, Box } from "@showitinc/showit-wc";
import cx from "classnames";
import uiPrefix from "../../../utils/UiPrefixUtils";
import styles from "./ElementPropertiesLocking.module.css";

// Import the vertical locking icons from public directory
const verticalLockingIcons = "/vertical-locking-icons.svg";

const VERTI_LOCK = "locking-vertical-";
const VERTI_LOCK_TOP = "t";
const VERTI_LOCK_TOP_STRETCH = "ts";
const VERTI_LOCK_BOTTOM = "b";
const VERTI_LOCK_BOTTOM_STRETCH = "bs";
const VERTI_LOCK_STRETCH = "s";

/**
 * Renders the vertical locking controls for element properties.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isLocked - Indicates if the element is locked.
 * @param {Function} props.clickLocking - Callback function to handle locking click events.
 * @param {Function} props.checked - Function to determine if a locking option is checked.
 * @param {string} props.elemLockV - Current vertical locking state of the element.
 * @param {boolean} props.disabled - If the element is disabled
 * @param {boolean} props.allowStretch - If the element has stretch options
 *
 * @returns {JSX.Element} A div containing vertical locking controls.
 *
 * @description
 * This component displays a set of buttons for controlling the vertical locking of an element.
 * It includes options for locking to top, bottom, stretching from top, stretching from bottom,
 * and full vertical stretch.
 *
 * @example
 * <ElementPropertiesVerticalLocking
 *   isLocked={false}
 *   clickLocking={(type, value) => handleLocking(type, value)}
 *   checked={(current, value) => current === value ? 'checked' : ''}
 *   elemLockV="t"
 * />
 *
 */
const ElementPropertiesVerticalLocking = ({
  isLocked,
  clickLocking,
  checked,
  elemLockV,
  disabled,
  allowStretch = true,
}) => (
  <>
    <Text className="panel-subheading">Vertical Locking</Text>
    <Box className={`${isLocked && styles.disabled} ${styles["v-icons-grid"]}`} mb={4}>
      <div
        data-e2e={uiPrefix(`${VERTI_LOCK}top-btn`)}
        className={`${uiPrefix(`${VERTI_LOCK}top-btn`)} ${styles["lock-to-top"]} ${
          styles["locking-button"]
        } ${styles.v}  ${checked(elemLockV, VERTI_LOCK_TOP)}        
        ${disabled && styles["disabled-button"]}
        `}
        onClick={() => clickLocking("lockV", VERTI_LOCK_TOP)}
      >
        <svg className={cx([styles["lock-to-top"], styles["svg-icon"], styles.v])}>
          <use href={`${verticalLockingIcons}#locktotop`} />
        </svg>
      </div>
      <div
        data-e2e={uiPrefix(`${VERTI_LOCK}bottom-btn`)}
        className={`${uiPrefix(`${VERTI_LOCK}bottom-btn`)} ${styles["lock-to-bottom"]} ${
          styles["locking-button"]
        } ${styles.v} ${checked(elemLockV, VERTI_LOCK_BOTTOM)}
        ${disabled && styles["disabled-button"]}
        `}
        onClick={() => clickLocking("lockV", VERTI_LOCK_BOTTOM)}
      >
        <svg className={cx([styles["lock-to-bottom"], styles["svg-icon"], styles.v])}>
          <use href={`${verticalLockingIcons}#locktobottom`} />
        </svg>
      </div>
      {allowStretch && (
        <>
          <div
            data-e2e={uiPrefix(`${VERTI_LOCK}stretch-top-btn`)}
            className={`${uiPrefix(`${VERTI_LOCK}stretch-top-btn`)} ${styles["stretch-top"]} ${
              styles["locking-button"]
            } ${styles.v} ${checked(elemLockV, VERTI_LOCK_TOP_STRETCH)}
        ${disabled && styles["disabled-button"]}
        `}
            onClick={() => clickLocking("lockV", VERTI_LOCK_TOP_STRETCH)}
          >
            <svg className={cx([styles["stretch-top"], styles["svg-icon"], styles.v])}>
              <use href={`${verticalLockingIcons}#stretchtotop`} />
            </svg>
          </div>
          <div
            data-e2e={uiPrefix(`${VERTI_LOCK}stretch-bottom-btn`)}
            className={`${uiPrefix(`${VERTI_LOCK}stretch-bottom-btn`)} ${
              styles["stretch-bottom"]
            } ${styles["locking-button"]} ${styles.v} ${checked(
              elemLockV,
              VERTI_LOCK_BOTTOM_STRETCH
            )}
        ${disabled && styles["disabled-button"]}
        `}
            onClick={() => clickLocking("lockV", VERTI_LOCK_BOTTOM_STRETCH)}
          >
            <svg className={cx([styles["stretch-bottom"], styles["svg-icon"], styles.v])}>
              <use href={`${verticalLockingIcons}#stretchtobottom`} />
            </svg>
          </div>
          <div
            data-e2e={uiPrefix(`${VERTI_LOCK}stretch-full-btn`)}
            className={`${uiPrefix(`${VERTI_LOCK}stretch-full-btn`)} ${styles["full-stretch-v"]} ${
              styles["locking-button"]
            } ${styles.fullv} ${checked(elemLockV, VERTI_LOCK_STRETCH)}
        ${disabled && styles["disabled-button"]}
        `}
            onClick={() => clickLocking("lockV", VERTI_LOCK_STRETCH)}
          >
            <svg className={cx([styles["full-stretch-v"], styles["svg-icon"], styles.fullv])}>
              <use href={`${verticalLockingIcons}#stretchfull`} />
            </svg>
          </div>
        </>
      )}
    </Box>
  </>
);
export default ElementPropertiesVerticalLocking;
